/**
 * Protected Route Component
 *
 * Route guard component that protects routes requiring authentication.
 * Redirects unauthenticated users to the login page with return URL, after
 * asking the server once per page load for a session the persisted store
 * doesn't know about (a web social sign-in makes one during its redirect).
 * A session the store knows is read again behind the page, so what changed
 * elsewhere shows (`useSessionPickup`).
 *
 * An account whose email isn't verified meets the gate in place of the page
 * (`EmailVerificationGate`): the API refuses it almost every call, so nothing
 * behind the gate may load. Verified, it's lifted where the account was.
 */

import React, { useRef } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { useSessionPickup } from '../useSessionPickup';

import type { ProtectedRouteProps } from './ProtectedRoute.types';

import { EmailVerificationGate } from '@/components/auth';
import { useAuth } from '@/hooks/auth';
import { useAuthStore } from '@/stores';
import { PUBLIC_ROUTES } from '@/utils';

/**
 * Protected route component that requires authentication
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  fallback,
  redirectTo = PUBLIC_ROUTES.LOGIN,
  requireAuth = true,
}) => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  // By the time this redirects, the user is already gone from the store. The
  // last one seen is who the page belonged to, so the login page can tell
  // whether the account that signs in next may return to it.
  const username = useAuthStore((state) => state.user?.username ?? null);
  const lastUserRef = useRef<string | null>(null);
  if (username) lastUserRef.current = username;

  // A user read before the field existed is waved through: the read behind
  // the page brings the field.
  const emailUnverified = useAuthStore(
    (state) => state.user?.email_verified === false
  );

  // Signed out in the store may still be signed in on the server, so ask
  // before sending the browser to log in. Signed in, the read runs behind the
  // page.
  const { isChecking } = useSessionPickup(
    requireAuth && !isAuthenticated && !isLoading
  );

  // Show loading spinner while checking authentication
  if (isLoading || isChecking) {
    return (
      fallback || (
        <div className="min-h-screen bg-contrast-950 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
          <div className="sm:mx-auto sm:w-full sm:max-w-md">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-stroke-strong mx-auto" />
              <p className="mt-4 text-label-sm text-neutral-400">
                Checking authentication...
              </p>
            </div>
          </div>
        </div>
      )
    );
  }

  // If authentication is required but user is not authenticated, redirect to login
  if (requireAuth && !isAuthenticated) {
    return (
      <Navigate
        to={redirectTo}
        state={{ from: location, fromUser: lastUserRef.current }}
        replace
      />
    );
  }

  if (requireAuth && emailUnverified) return <EmailVerificationGate />;

  // If authentication is not required or user is authenticated, render children
  return <>{children}</>;
};

export default ProtectedRoute;
